// embedMode.js — a page drawn as a slide of the Opposition Preview deck.
//
// The deck's set-piece and physicals slides ARE those pages: the preview loads each one in a frame
// at slide proportions with `?opvEmbed=1` and the state the slide needs (team, competition, side,
// view) in the query, and the page opens on exactly that. In embed mode the app shell drops the
// sidebar and every page toolbar (the slide carries its own title), and the window answers two
// questions for the deck:
//
//   window.__opvReady()    true once nothing on the page is still loading (no spinner showing)
//   window.__opvCapture()  a PNG data URL of the page, through the app's own export capture — the
//                          same html2canvas path, with the same sanitising, every report export uses
//
// Read once at load: a frame is created per slide, so its query never changes under the page.

const PARAMS = (() => {
  try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(); }
})();

export const EMBED = PARAMS.has('opvEmbed');

/** A query value when the page is embedded, else null — so outside the deck nothing changes. */
export function embedParam(name) {
  return EMBED ? PARAMS.get(name) : null;
}

/** The match ids the deck narrowed this slide to (`name` holds a comma list), or [] — no filter —
 *  outside the deck or when the deck has none. Ids are kept as the strings they came as: they only
 *  ever go back into a query string. */
export function embedIds(name) {
  const v = embedParam(name);
  return v ? v.split(',').map((x) => x.trim()).filter(Boolean) : [];
}

const LOADING = '.sp-ring, .sp-block, .sp-line, .loading-overlay';

function visible(el) {
  return !!(el && (el.offsetParent || el.getClientRects().length));
}

/** Install the two hooks the deck calls. `root` is the element to picture. */
export function installEmbedBridge(getRoot) {
  if (!EMBED) return;
  document.documentElement.classList.add('opv-embed');
  window.__opvReady = () => {
    const root = getRoot();
    if (!root || !root.querySelector('*')) return false;
    // A page still reading its own pickers (team, competition) shows nothing at all — no content and
    // no spinner — so "no spinner" alone would picture an empty page. Something must be on show.
    if (!(root.innerText || '').trim()) return false;
    return ![...document.querySelectorAll(LOADING)].some(visible);
  };
  window.__opvCapture = async (scale = 2) => {
    const { captureElementCanvas } = await import('./exportHtml');
    const canvas = await captureElementCanvas(getRoot(), scale);
    return canvas.toDataURL('image/png');
  };
}
